class PredictiveSearch extends SearchForm {
  constructor() {
    super();
    this.cachedResults = {};
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.suggestions = this.querySelector('[data-search-suggestions]');
    this.allPredictiveSearchInstances = document.querySelectorAll('predictive-search');
    this.isOpen = false;
    this.abortController = new AbortController();
    this.searchTerm = '';

    this.setupEventListeners();
  }

  setupEventListeners() {
    this.input.form.addEventListener('submit', this.onFormSubmit.bind(this));

    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.addEventListener('keyup', this.onKeyup.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));

    this.predictiveSearchResults.addEventListener('click', this.onResultClick.bind(this));

    if (this.suggestions) {
      // Keep the focus on the input so the suggestions don't close before the click is handled
      this.suggestions.addEventListener('mousedown', (event) => event.preventDefault());
      this.suggestions.addEventListener('click', this.onSuggestionClick.bind(this));
    }
  }

  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    super.onChange();
    const newSearchTerm = this.getQuery();
    if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
      // Remove the results when they are no longer relevant for the new search term
      // so they don't show up when the dropdown opens again
      this.querySelector('#predictive-search-results-groups-wrapper')?.remove();
    }

    // Update the term asap, don't wait for the predictive search query to finish loading
    this.updateSearchForTerm(this.searchTerm, newSearchTerm);

    this.searchTerm = newSearchTerm;

    if (!this.searchTerm.length) {
      this.close(true);
      this.openSuggestions();
      return;
    }

    this.closeSuggestions();
    this.getSearchResults(this.searchTerm);
  }

  onFormSubmit(event) {
    if (!this.getQuery().length || this.querySelector('[aria-selected="true"] a')) {
      event.preventDefault();
      return;
    }

    this.saveRecentSearch(this.getQuery());
  }

  onFormReset(event) {
    super.onFormReset(event);
    if (super.shouldResetForm()) {
      this.searchTerm = '';
      this.abortController.abort();
      this.abortController = new AbortController();
      this.closeResults(true);
      this.openSuggestions();
    }
  }

  onFocus() {
    const currentSearchTerm = this.getQuery();

    if (!currentSearchTerm.length) {
      this.openSuggestions();
      return;
    }

    if (this.searchTerm !== currentSearchTerm) {
      // Search term was changed from other search input, treat it as a user change
      this.onChange();
    } else if (this.getAttribute('results') === 'true') {
      this.open();
    } else {
      this.getSearchResults(this.searchTerm);
    }
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  onKeyup(event) {
    if (!this.getQuery().length) {
      this.close(true);
      this.openSuggestions();
    }
    event.preventDefault();

    switch (event.code) {
      case 'Escape':
        this.close();
        this.input.blur();
        break;
      case 'ArrowUp':
        this.switchOption('up');
        break;
      case 'ArrowDown':
        this.switchOption('down');
        break;
      case 'Enter':
        this.selectOption();
        break;
    }
  }

  onKeydown(event) {
    // Prevent the cursor from moving in the input when using the up and down arrow keys
    if (event.code === 'ArrowUp' || event.code === 'ArrowDown') {
      event.preventDefault();
    }
  }

  onResultClick(event) {
    if (event.target.closest('a')) this.saveRecentSearch(this.searchTerm);
  }

  onSuggestionClick(event) {
    if (event.target.closest('[data-recent-clear]')) {
      this.clearRecentSearches();
      return;
    }

    const suggestion = event.target.closest('[data-search-term]');
    if (!suggestion) return;

    this.input.value = suggestion.dataset.searchTerm;
    this.input.focus();
    // Focusing the input already triggers the search when the focus was on the suggestion
    if (this.searchTerm !== this.getQuery()) this.onChange();
  }

  updateSearchForTerm(previousTerm, newTerm) {
    const searchForTextElement = this.querySelector('[data-predictive-search-search-for-text]');
    const currentButtonText = searchForTextElement?.innerText;
    if (currentButtonText && previousTerm) {
      if (currentButtonText.split(previousTerm).length > 2) {
        // The new term matches part of the button text and not just the search term, do not replace to avoid mistakes
        return;
      }
      const newButtonText = currentButtonText.replace(previousTerm, newTerm);
      searchForTextElement.innerText = newButtonText;
    }
  }

  switchOption(direction) {
    if (!this.getAttribute('open')) return;

    const moveUp = direction === 'up';
    const selectedElement = this.querySelector('[aria-selected="true"]');

    // Filter out hidden elements (duplicated page and article resources) thanks
    // to this https://developer.mozilla.org/en-US/docs/Web/API/HTMLElement/offsetParent
    const allVisibleElements = Array.from(this.querySelectorAll('li, button.predictive-search__item')).filter(
      (element) => element.offsetParent !== null
    );
    let activeElementIndex = 0;

    if (moveUp && !selectedElement) return;

    let selectedElementIndex = -1;
    let i = 0;

    while (selectedElementIndex === -1 && i <= allVisibleElements.length) {
      if (allVisibleElements[i] === selectedElement) {
        selectedElementIndex = i;
      }
      i++;
    }

    this.statusElement.textContent = '';

    if (!moveUp && selectedElement) {
      activeElementIndex = selectedElementIndex === allVisibleElements.length - 1 ? 0 : selectedElementIndex + 1;
    } else if (moveUp) {
      activeElementIndex = selectedElementIndex === 0 ? allVisibleElements.length - 1 : selectedElementIndex - 1;
    }

    if (activeElementIndex === selectedElementIndex) return;

    const activeElement = allVisibleElements[activeElementIndex];

    activeElement.setAttribute('aria-selected', true);
    if (selectedElement) selectedElement.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', activeElement.id);
  }

  selectOption() {
    const selectedOption = this.querySelector('[aria-selected="true"] a, button[aria-selected="true"]');

    if (selectedOption) selectedOption.click();
  }

  getSearchResults(searchTerm) {
    const queryKey = searchTerm.replace(' ', '-').toLowerCase();
    this.setLiveRegionLoadingState();

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey]);
      return;
    }

    const searchParams = new URLSearchParams({
      q: searchTerm,
      section_id: 'predictive-search',
      'resources[limit]': 6,
      'resources[limit_scope]': 'each',
      'resources[options][unavailable_products]': 'last',
      'resources[options][fields]': 'title,product_type,variants.title,variants.sku,vendor,tag',
    });

    fetch(`${routes.predictive_search_url}?${searchParams}`, {
      signal: this.abortController.signal,
    })
      .then((response) => {
        if (!response.ok) {
          var error = new Error(response.status);
          this.close();
          throw error;
        }

        return response.text();
      })
      .then((text) => {
        const resultsMarkup = new DOMParser()
          .parseFromString(text, 'text/html')
          .querySelector('#shopify-section-predictive-search').innerHTML;
        // Save bandwidth keeping the cache in all instances synced
        this.allPredictiveSearchInstances.forEach((predictiveSearchInstance) => {
          predictiveSearchInstance.cachedResults[queryKey] = resultsMarkup;
        });
        this.renderSearchResults(resultsMarkup);
      })
      .catch((error) => {
        if (error?.code === 20) {
          // Code 20 means the call was aborted
          return;
        }
        this.close();
        throw error;
      });
  }

  setLiveRegionLoadingState() {
    this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
    this.loadingText = this.loadingText || this.getAttribute('data-loading-text');

    this.setLiveRegionText(this.loadingText);
    this.setAttribute('loading', true);
  }

  setLiveRegionText(statusText) {
    this.statusElement.setAttribute('aria-hidden', 'false');
    this.statusElement.textContent = statusText;

    setTimeout(() => {
      this.statusElement.setAttribute('aria-hidden', 'true');
    }, 1000);
  }

  renderSearchResults(resultsMarkup) {
    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.setAttribute('results', true);

    this.highlightSearchTerm();
    this.setLiveRegionResults();
    this.open();
  }

  highlightSearchTerm() {
    const terms = this.searchTerm
      .split(/\s+/)
      .filter(Boolean)
      .map((term) => term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
    if (!terms.length) return;

    const termsPattern = new RegExp(`(${terms.join('|')})`, 'gi');

    this.predictiveSearchResults
      .querySelectorAll('.predictive-search__item-heading:not(.predictive-search__item-query-result)')
      .forEach((heading) => {
        // Splitting on a capture group leaves the matches at the odd indexes
        const parts = heading.textContent.split(termsPattern).map((part, index) => {
          if (index % 2 === 0) return part;
          const mark = document.createElement('mark');
          mark.textContent = part;
          return mark;
        });
        heading.replaceChildren(...parts);
      });
  }

  setLiveRegionResults() {
    this.removeAttribute('loading');
    this.setLiveRegionText(this.querySelector('[data-predictive-search-live-region-count-value]').textContent);
  }

  getResultsMaxHeight() {
    // The visual viewport excludes the on-screen keyboard, so the results stay reachable on mobile
    const viewportHeight = window.visualViewport?.height || window.innerHeight;
    this.resultsMaxHeight = viewportHeight - document.querySelector('.section-header')?.getBoundingClientRect().bottom;
    return this.resultsMaxHeight;
  }

  getRecentSearches() {
    try {
      const recentSearches = JSON.parse(localStorage.getItem(PredictiveSearch.recentSearchesKey));
      return Array.isArray(recentSearches) ? recentSearches.filter((term) => typeof term === 'string') : [];
    } catch (error) {
      return [];
    }
  }

  saveRecentSearch(searchTerm) {
    if (!this.suggestions || !searchTerm.length) return;

    const recentSearches = this.getRecentSearches().filter((term) => term.toLowerCase() !== searchTerm.toLowerCase());
    recentSearches.unshift(searchTerm);

    try {
      localStorage.setItem(PredictiveSearch.recentSearchesKey, JSON.stringify(recentSearches.slice(0, 5)));
    } catch (error) {
      // Storage can be unavailable (private browsing), recent searches are skipped then
    }
  }

  clearRecentSearches() {
    try {
      localStorage.removeItem(PredictiveSearch.recentSearchesKey);
    } catch (error) {
      // Nothing was stored if the storage is unavailable
    }

    this.closeSuggestions();
    this.openSuggestions();
  }

  renderRecentSearches() {
    const recentSearchesGroup = this.suggestions.querySelector('[data-recent-searches]');
    const recentSearches = this.getRecentSearches();

    recentSearchesGroup.querySelector('ul').replaceChildren(
      ...recentSearches.map((term) => {
        const listItem = document.createElement('li');
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'search-bar__chip';
        button.dataset.searchTerm = term;
        button.textContent = term;
        listItem.appendChild(button);
        return listItem;
      })
    );
    recentSearchesGroup.hidden = !recentSearches.length;
  }

  openSuggestions() {
    if (!this.suggestions || this.getQuery().length || document.activeElement !== this.input) return;

    this.renderRecentSearches();
    // Nothing to suggest without recent searches or popular links
    if (!this.suggestions.querySelector(':scope > :not([hidden])')) return;

    this.setAttribute('suggest', true);
    this.isOpen = true;
  }

  closeSuggestions() {
    this.removeAttribute('suggest');
  }

  open() {
    this.predictiveSearchResults.style.maxHeight = this.resultsMaxHeight || `${this.getResultsMaxHeight()}px`;
    this.setAttribute('open', true);
    this.input.setAttribute('aria-expanded', true);
    this.isOpen = true;
  }

  close(clearSearchTerm = false) {
    this.closeResults(clearSearchTerm);
    this.closeSuggestions();
    this.isOpen = false;
  }

  closeResults(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = '';
      this.removeAttribute('results');
    }
    const selected = this.querySelector('[aria-selected="true"]');

    if (selected) selected.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', '');
    this.removeAttribute('loading');
    this.removeAttribute('open');
    this.input.setAttribute('aria-expanded', false);
    this.resultsMaxHeight = false;
    this.predictiveSearchResults.removeAttribute('style');
  }
}

PredictiveSearch.recentSearchesKey = 'recent-searches';

customElements.define('predictive-search', PredictiveSearch);
